<script setup lang="ts">
import type { MonsterType } from '@/types/monster'
import type { FightInfoType, FightJoinRes, FightCoreRes, DamageRankType, FightResType } from '@/types/fight'
import { ref, watch, nextTick } from 'vue'
import { formatMs, formatMs2Hour } from '@/utils'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import { useCommonStore } from '@/stores/modules/common'
import { computed } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import { autoFightHourList } from '../data'
import { dayjs } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import { fightExplain } from '@/core/fight'
import AutoChangeRoutine from './AutoChangeRoutine.vue'
import FightSkillInfo from './FightSkillInfo.vue'

const commonStore = useCommonStore()
const userStore = useUserStore()
const emits = defineEmits(['showOtherInfo'])

const monster = ref<MonsterType>()
const monsterHp = ref(0)
const monsterCurHp = ref(0)
const monsterCurHpPercent = computed(() => (monsterCurHp.value / monsterHp.value) * 100)
const startTime = ref(0)
const startTimeInterval = ref()
const autoFightEndTime = ref(0)
const isJoin = ref(false)
const joinTime = ref(0)
const loading = ref(false)
const isTouchMsg = ref(false)

async function getFightInfo() {
  const res = await socketEmitAsync<FightInfoType | undefined>({ type: 'getFightInfo' })
  if (res) setFightInfo(res)
}
async function joinFight() {
  try {
    loading.value = true
    const res = await socketEmitAsync<number>({ type: 'joinFight' })
    isJoin.value = true
    joinTime.value = res
  } finally {
    loading.value = false
  }
}
function setFightInfo(fightInfo: FightInfoType) {
  monster.value = fightInfo.monster
  monsterHp.value = fightInfo.monsterHp
  monsterCurHp.value = fightInfo.monsterCurHp
  startTime.value = dayjs().diff(dayjs(fightInfo.created))
  clearInterval(startTimeInterval.value)
  startTimeInterval.value = setInterval(() => {
    startTime.value = dayjs().diff(dayjs(fightInfo.created))
    if (autoFightEndTime.value >= 0) {
      autoFightEndTime.value = Number.isNaN(dayjs(userStore.autoEndTime).diff(dayjs())) ? 0 : dayjs(userStore.autoEndTime).diff(dayjs())
    }
  }, 1000)
  if (typeof fightInfo.isJoin !== 'undefined') isJoin.value = fightInfo.isJoin
  joinTime.value = fightInfo.joinTime
}

const fightMessageList = ref<string[]>([])
const curMp = ref(userStore.userAttr.mp)
const curCdList = ref<[number, number][]>([])
const playerTotal = ref(0)
const damageRank = ref<DamageRankType[]>([])

function handleAutoFight(hour: number, noConfirm = false) {
  if (noConfirm) {
    return autoFight()
  }
  ElMessageBox.confirm(`确认开启 <b>${hour}</b> 小时的自动参战吗？（可离线自动）`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(() => {
      autoFight()
    })
    .catch(() => {})

  async function autoFight() {
    const res = await socketEmitAsync<string>({ type: 'autoFight', data: { hour } }).catch((err) => {
      if (err.includes('无权限')) {
        ElMessageBox.alert('您可以免费使用8小时自动参战，请加入官方Q群了解更高时长的自动参战权限', '提示')
      }
    })
    if (!res) return
    userStore.autoEndTime = res || ''
    autoFightEndTime.value = Number.isNaN(dayjs(res).diff(dayjs())) ? 0 : dayjs(res).diff(dayjs())
    isJoin.value = true
  }
}
function cancelAutoFight() {
  ElMessageBox.confirm('确认取消自动参战吗？', '提示', {
    type: 'warning',
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync<string>({ type: 'cancelAutoFight' })
      userStore.autoEndTime = ''
    })
    .catch(() => {})
}

const secondDamage = computed(() => {
  if (!isJoin.value) return 0
  const totalDamage = damageRank.value.find((item) => item.id === userStore.id)?.damage || 0
  return (totalDamage / ((startTime.value - joinTime.value) / 1000)).toFixed(1)
})

const setShow = ref(false)
const monsterList = ref<MonsterType[]>([])
async function handleShowSet() {
  const res = await socketEmitAsync<MonsterType[]>({ type: 'getFightMonsterList' })
  res.forEach((item) => {
    userStore.autoChangeRoutineList[item.id] = userStore.autoChangeRoutineList[item.id] || 0
  })
  monsterList.value = res
  setShow.value = true
}

watch(
  () => commonStore.isSocketInit,
  async (val) => {
    if (!val) return

    getFightInfo()

    socketOn({
      type: 'fightCore',
      callBack: (res: FightCoreRes) => {
        playerTotal.value = res.playerTotal
        monsterCurHp.value = res.monsterCurHp
        damageRank.value = res.damageRank.map((item) => ({
          ...item,
          percent: `${((item.damage / monsterHp.value) * 100).toFixed(2)}%`,
        }))
      },
    })
    socketOn({
      type: 'fightRes',
      callBack: async (res: FightResType) => {
        const fightText = fightExplain(res.atkInfoList, res.element)
        fightMessageList.value.push(fightText)
        curMp.value = res.mp
        curCdList.value = res.cdList

        const length = fightMessageList.value.length
        if (length > 60) {
          fightMessageList.value = fightMessageList.value.slice(Math.max(length - 30, 0))
        }

        const messageBoxEl = document.getElementById('messageBox')
        await nextTick()
        if (!messageBoxEl || isTouchMsg.value) return
        messageBoxEl.scrollTo({
          top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
          behavior: 'smooth',
        })
      },
    })
    socketOn({
      type: 'fightCreate',
      callBack: (res: FightInfoType) => {
        setFightInfo(res)
        fightMessageList.value.push('战斗开始')
        loading.value = false
      },
    })
    socketOn({
      type: 'fightJoin',
      callBack: (res: FightJoinRes) => {
        monsterHp.value = res.monsterHp
        monsterCurHp.value = res.monsterCurHp
        fightMessageList.value.push(res.message)
        loading.value = false
      },
    })
    socketOn({
      type: 'fightOver',
      callBack: () => {
        monster.value = undefined
        monsterHp.value = 0
        monsterCurHp.value = 0
        fightMessageList.value.push('战斗结束')
        if (!autoFightEndTime.value) isJoin.value = false
        clearInterval(startTimeInterval.value)
        setTimeout(() => {
          commonStore.getMailList()
        }, Math.random() * 10 * 1000)
      },
    })
  },
)
watch(
  () => commonStore.isSocketReconnect,
  async (val) => {
    if (!val) return
    getFightInfo()
  },
)
watch(
  () => commonStore.path,
  (val) => {
    if (val === 'Fight' && userStore.autoEndTime) {
      const vipLevel = userStore.vipLevel
      const hour = autoFightHourList.find((item) => item.vipLevel === vipLevel)?.value
      if (!hour) return
      const remainder = dayjs(userStore.autoEndTime).diff(dayjs(), 'hour')
      if (vipLevel) {
        if ((vipLevel === 1 && remainder <= 12) || (vipLevel === 2 && remainder <= 24) || (vipLevel === 3 && remainder <= 36)) {
          handleAutoFight(hour, true)
        }
      } else {
        if (remainder <= 4) {
          handleAutoFight(hour, true)
        }
      }
    }
  },
)
</script>

<template>
  <div class="world-boss">
    <div class="monster-info">
      <MonsterDetail :monster="monster" style="margin-bottom: 10px"></MonsterDetail>
      <el-progress :text-inside="true" :stroke-width="20" :percentage="(monsterCurHpPercent < 0 ? 0 : monsterCurHpPercent) || 0" status="exception">
        <span>{{ monster && monsterCurHpPercent ? monsterCurHpPercent.toFixed(2) : 0 }} %</span>
      </el-progress>
      <p class="monster-extra-info" :style="{ visibility: monster ? 'unset' : 'hidden' }">
        <span>{{ formatMs(startTime) }}</span>
        <span>{{ monsterCurHp }} / {{ monsterHp }}</span>
      </p>
    </div>
    <div class="help-info">
      <el-popover placement="bottom" width="100%" trigger="click">
        <template #reference>
          <el-icon size="25"><QuestionFilled /></el-icon>
        </template>
        <div class="affix">
          <h4>共斗说明</h4>
          <p>1. 通过点击怪物按钮，可查看怪物属性。</p>
          <p>
            2. boss被击败结算时，会根据玩家伤害占比进行平均<span class="gold">金币分红</span
            >。伤害排名主要是给大家提供一个调节自身装备的平台，排名本身不会带来太过不平衡的收益。
          </p>
          <p>
            3. 在隐私设置中<span class="grow">允许查看穿戴装备</span>，可额外获得<span class="grow">10%</span>共斗<span class="gold">金币</span>加成。
          </p>
          <p>4. 共斗奖励分为两种：个人奖励、 团队奖励，团队奖励的爆率远高于个人奖励，均会在boss被击败时通过邮件发放。</p>
          <p style="text-indent: 2em">
            a. 个人奖励：当玩家对boss造成boss基础血量的10%时，会增加一次个人奖励次数，个人奖励会获得boss基础分红<span class="info">10%</span
            >的金币，同时有<span class="info">45%</span>的概率掉落<span class="grow">1</span>件装备。
          </p>
          <p style="text-indent: 2em">
            b. 团队奖励：当boss被击败时，会根据玩家的伤害占比进行金币分红，金币分红 = 参战人数 * boss基础分红。另外会掉落<span class="grow">3-6</span
            >件装备，同时装备的掉落品质会根据参战人数影响，越多玩家参与则更容易掉落品质更高的装备。
          </p>
          <p>
            5. 个人奖励爆率：<span class="normal">普通</span> 87.4%，<span class="race">稀有</span> 10%，<span class="epic">史诗</span> 2%，<span
              class="unique"
              >独特</span
            >
            0.4%，<span class="legend">传说</span> 0.2%。
          </p>
          <p>
            6. 团队奖励基础爆率：<span class="normal">普通</span> 70.4，<span class="race">稀有</span> 22%，<span class="epic">史诗</span> 5%，<span
              class="unique"
              >独特</span
            >
            1.8%，<span class="legend">传说</span> 0.8%。
          </p>
        </div>
      </el-popover>
    </div>
    <div class="fight-user-list border-wrap">
      <h4>伤害排行</h4>
      <p style="display: flex; justify-content: space-between; margin: 2px; font-size: 12px">
        <span>参战人数：{{ playerTotal }}</span>
        <span>每秒伤害：{{ secondDamage }}</span>
      </p>
      <el-table
        :data="damageRank"
        border
        :style="{ width: '100%', height: '25vh' }"
        :row-class-name="({ row }: any) => (row.id === userStore.id ? 'personal' : '')"
        size="small"
        @row-click="(row: any) => emits('showOtherInfo', row)"
      >
        <el-table-column prop="rank" label="排位" width="50" />
        <el-table-column prop="nickname" label="昵称">
          <template #default="{ row }">
            <span class="nickname" :style="{ background: row.color }">{{ row.nickname }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="damage" label="伤害" width="80" />
        <el-table-column prop="percent" label="占比" width="70" />
      </el-table>
    </div>
    <div class="fight-res-info-wrap">
      <h4>战斗信息</h4>
      <div class="fight-res-info affix" id="messageBox" @touchstart="isTouchMsg = true" @touchend="isTouchMsg = false">
        <p v-for="(item, index) of fightMessageList" :key="index" v-html="item"></p>
      </div>
      <FightSkillInfo :mp="curMp" :cd-list="curCdList" v-if="userStore.soulType > 3" />
    </div>
    <div class="fight-btn-wrap" v-loading="loading">
      <el-button type="warning" plain @click="handleShowSet">设置</el-button>
      <template v-if="autoFightEndTime <= 0">
        <el-button class="main" type="success" plain @click="joinFight" :disabled="isJoin">
          {{ isJoin ? '已参战' : '参加战斗' }}
        </el-button>
        <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
          <template #reference>
            <el-button type="primary" plain>自动</el-button>
          </template>
          <div class="hour-list affix">
            <el-button size="small" type="primary" plain v-for="item of autoFightHourList" :key="item.value" @click="handleAutoFight(item.value)">
              <span :class="item.class">{{ item.value }} 小时</span>
            </el-button>
          </div>
        </el-popover>
      </template>
      <template v-else>
        <el-button class="main" type="primary" plain disabled>自动参战中（{{ formatMs2Hour(autoFightEndTime) }}）</el-button>
        <el-button type="danger" plain @click="cancelAutoFight">取消</el-button>
      </template>
    </div>

    <el-dialog v-model="setShow" width="100%" style="max-width: 500px">
      <AutoChangeRoutine :monster-list="monsterList" type="normal" />

      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="setShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.monster-info {
  :deep() {
    h2 {
      margin: 0;
    }
    button {
      margin-bottom: 10px;
    }
  }
}
</style>
